import * as React from 'react';
import Header from '@appComponents/Header/Header';
import { InitialState } from './components/InitialState/InitialState';
import Loader from '@appComponents/Loader/Loader';
import { AcceptedState } from './components/AcceptedState/AcceptedState';
import { RejectedState } from './components/RejectedState/RejectedState';
import { RequiredSnapshotState } from './components/RequiredSnapshotState/RequiredSnapshotState';
import { FaqSection } from './components/FaqSection/FaqSection';
import { CelsiusClaim } from './components/CelsiusClaim/CelsiusClaim';
import CelsiusClaimService from '@services/CelsiusClaim.service';
import { PendingState } from './components/RequiredSnapshotState/components/PendingState/PendingState';
import { UnverifiedState } from './components/UnverifiedState/UnverifiedState';
import { connect } from 'react-redux';
import { TReducers } from '@features/reducers';
import { ETiers } from '@appComponents/components/IdentityStatusLabel/IIdentityStatusLabel';
import { hasRequiredTier } from '@features/utils/functions';

import * as style from './CelsiusClaimPageStyles.module.scss'

export type CelsiusClaimStatus =
  | 'pending'
  | 'accepted'
  | 'rejected'
  | 'awaiting_screenshot'
  | 'initial'
  | 'loading';

type CurrencyName = {
  decimal_mark: string;
  name: string;
  symbol: string;
  symbol_first: false;
  thousands_separator: string;
};

export type BalanceType =
  | 'earn'
  | 'custody'
  | 'withheld'
  | 'collateral'
  | 'ai_cel_earned';

export type CelsiusClaimBalance = {
  available_balance: string;
  balance: string;
  currency: string;
  currency_name: CurrencyName;
  balance_type: BalanceType;
  current_balance: string;
  id: number;
  total_usd: string;
};

export type CelsiusClaimData = {
  loss_in_usd_earn: number;
  loss_in_usd_custody: number;
  loss_in_usd_withheld: number;
  loss_in_usd_loan: number;
  loss_in_usd_collateral: number;
  loss_in_usd_post_set: number;
}

type Props = {
  isVerified: boolean;
};

const CelsiusClaimPage = ({ isVerified }: Props) => {
  const emptyClaimData = {
    loss_in_usd_earn: 0,
    loss_in_usd_custody: 0,
    loss_in_usd_withheld: 0,
    loss_in_usd_loan: 0,
    loss_in_usd_collateral: 0,
    loss_in_usd_post_set: 0
  }


  const [balances, setBalances] = React.useState<{ balances: CelsiusClaimBalance[] }>({ balances: [] });
  const [claimData, setClaimData] = React.useState<CelsiusClaimData>(emptyClaimData);
  const [readMore, setReadMore] = React.useState(false);
  const [rejectionReason, setRejectionReason] = React.useState<string>();
  const [firstFetchSuccess, setFirstFetchSuccess] = React.useState(false);
  const [claimStatus, setClaimStatus] = React.useState<CelsiusClaimStatus>(
    'loading'
  );

  React.useEffect(() => {
    CelsiusClaimService.getClaim()
      .then((resp) => {
        setClaimStatus(resp.data.status);
        setBalances({ balances: resp.data.balances });
        setClaimData(resp.data.claim)
        setRejectionReason(resp.data.rejection_reason);
      })
      .catch(() => setClaimStatus('initial'));
  }, []);

  const onImportFundsClick = () => {
    setClaimStatus('loading');
    CelsiusClaimService.postClaim()
      .then((resp) => {
        setClaimStatus(resp.data.status);
        setFirstFetchSuccess(true);
        setBalances({ balances: resp.data.balances });
      })
      .catch((err) => {
        throw new Error(err);
      });
  };

  return (
    <div className={style.pageWrapper}>
      <Header>
        {claimStatus === 'loading' && (
          <div className={style.loaderWrapper}>
            <Loader />
          </div>
        )}
        {claimStatus === 'initial' && isVerified && (
          <InitialState
            handleImportFunds={onImportFundsClick}
          />
        )}
        {['initial', 'rejected'].includes(claimStatus) && !isVerified && (
          <UnverifiedState />
        )}
        {claimStatus === 'accepted' && firstFetchSuccess && (
          <AcceptedState
            handleImportFunds={() => setFirstFetchSuccess(false)}
          />
        )}
        {claimStatus === 'accepted' && !firstFetchSuccess && (
          <CelsiusClaim balances={balances.balances} claimData={claimData} />
        )}
        {claimStatus === 'rejected' && (
          <RejectedState
            handleImportFunds={() => setClaimStatus('awaiting_screenshot')}
            rejectionReason={rejectionReason}
          />
        )}

        {claimStatus === 'pending' && <PendingState />}

        {claimStatus === 'awaiting_screenshot' && (
          <RequiredSnapshotState setClaimStatus={setClaimStatus} />
        )}
      </Header>
      <FaqSection />
    </div>
  );
};

const mapStateToProps = (store: TReducers) => ({
  isVerified: hasRequiredTier(ETiers.tier_0_kyc, store.userInfo.tier),
});

export default connect(mapStateToProps)(CelsiusClaimPage);
